// O termo de adesão que vale para uma unidade — o contrato que o cobrador mostra
// quando o cliente diz que nunca assinou.
//
// Pedido do gestor em 10/09/2026: "às vezes eles falam que não assinaram". Ter o
// PDF e a DATA da assinatura na mão encerra a conversa na porta.
//
// O termo mora em três lugares, e a busca tem que tentar os três (medido em
// 10/09/2026 sobre as 345 UCs com fatura vencida e cadastro):
//   • `unidades_consumidoras.url_termo` — 8 UCs. Quase vazio, mas é o mais
//     específico: quando existe, é o termo daquela unidade.
//   • a negociação vinculada por `unidades_consumidoras.negociacao_id` — 44 UCs,
//     e TODAS as 44 têm termo. O vínculo é raro, mas nunca é em vão.
//   • a negociação do mesmo CPF/CNPJ — mais 9. Vale porque o vínculo direto só
//     foi preenchido em parte da base.
// Somando, 53 das 345 (15%). Não é muito, e é exatamente por isso que a linha
// só aparece quando existe: prometer contrato para todo mundo criaria a
// expectativa errada em quem está com o cliente na frente.

/** Item de `url_termo` (jsonb) — o formato é o mesmo na UC e na negociação. */
export interface ItemTermo {
    url_pdf?: string | null;
    data_criacao?: string | null;
    numero_termo?: string | null;
    /** Marcado pelo admin como o termo efetivamente assinado (override do duplo envio). */
    validado?: boolean | null;
    /** Quando o cliente assinou no Autentique (ISO). */
    data_assinatura?: string | null;
    /** false = assinou, mas a biometria (SERPRO) ainda não voltou. */
    assinatura_verificada?: boolean | null;
}

export interface TermoAdesao {
    url: string;
    /** ISO, ou null quando a assinatura não foi registrada no item. */
    dataAssinatura: string | null;
    numero: string | null;
    validado: boolean;
    /** null = o item não diz nada sobre biometria. */
    verificada: boolean | null;
}

const texto = (v: unknown): string => String(v ?? '').trim();

/**
 * Qual dos termos vale, quando há mais de um.
 *
 * Há duplicidade de verdade na base — o mesmo cliente recebeu dois envios e as
 * duas linhas ficaram. A ordem de desempate reproduz a decisão humana que já
 * existe na tela (DefinirTermoValidoModal):
 *   1. `validado` ganha de tudo: é um admin dizendo "foi este".
 *   2. depois, o que tem assinatura — e, entre eles, a mais recente.
 *   3. por último, o mais recentemente criado.
 * Item sem `url_pdf` é descartado: não há o que mostrar ao cliente.
 */
export const escolherTermo = (
    lista: ItemTermo[] | null | undefined,
): TermoAdesao | null => {
    const itens = (Array.isArray(lista) ? lista : []).filter(t => texto(t?.url_pdf));
    if (itens.length === 0) return null;

    const ordenado = [...itens].sort((a, b) => {
        const va = a?.validado === true ? 1 : 0;
        const vb = b?.validado === true ? 1 : 0;
        if (va !== vb) return vb - va;

        const aa = texto(a?.data_assinatura);
        const ab = texto(b?.data_assinatura);
        if (!!aa !== !!ab) return ab ? 1 : -1;
        if (aa && ab && aa !== ab) return ab.localeCompare(aa);

        return texto(b?.data_criacao).localeCompare(texto(a?.data_criacao));
    });

    const t = ordenado[0];
    return {
        url: texto(t.url_pdf),
        dataAssinatura: texto(t.data_assinatura) || null,
        numero: texto(t.numero_termo) || null,
        validado: t.validado === true,
        verificada: t.assinatura_verificada == null ? null : t.assinatura_verificada === true,
    };
};

/**
 * O primeiro termo que aparecer, na ordem de confiança das fontes.
 *
 * A UC vem antes da negociação porque é mais específica: um cliente com várias
 * unidades tem um termo por unidade, e o da negociação pode ser de outra.
 */
export const termoMaisRelevante = (
    daUc: ItemTermo[] | null | undefined,
    daNegociacaoVinculada?: ItemTermo[] | null,
    daNegociacaoPorCpf?: ItemTermo[] | null,
): TermoAdesao | null =>
    escolherTermo(daUc)
    ?? escolherTermo(daNegociacaoVinculada)
    ?? escolherTermo(daNegociacaoPorCpf);
